import { Seo } from '../components/Seo';
import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { FlightSearchBar } from '../components/FlightSearchBar';
import { CountrySearchBar } from '../components/CountrySearchBar';
import { FlightDiscovery } from '../components/FlightDiscovery';
import { WeekendFinder } from '../components/WeekendFinder';
import { useApp } from '../context/AppContext';
import { AffiliateDisclosure } from '../components/AffiliateDisclosure';
import { TravelpayoutsWidget } from '../components/TravelpayoutsWidget';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Gauge, Layers, ShieldCheck, Plane, Globe2, AlertTriangle, Sparkles, Search, CalendarHeart } from 'lucide-react';

// Travelpayouts White Label flight metasearch (wl_id=21150) renders the search form +
// results in-page via the <TpwlSearch /> component, keeping users on FlightDeals.in.

const FEATURES = [
  { icon: Gauge, t: 'Transparent ranking', s: 'Results are ranked on price, duration, stops & schedule — never on commission.' },
  { icon: Layers, t: 'One search, many partners', s: 'We compare hundreds of travel sites so you don’t have to open ten tabs.' },
  { icon: ShieldCheck, t: 'Book with confidence', s: 'You complete your booking securely on the partner’s website.' },
];

export default function Flights() {
  const { currency } = useApp();
  const [tab, setTab] = useState('search');
  const [mode, setMode] = useState('airport');

  return (
    <Layout>
      <Seo title="Cheap Flights — Compare & Book" path="/flights"
        description="Search and compare cheap flights across airlines and partners with FlightDeals.in. Transparent pricing, no booking fees — book securely on partner sites." />
      <Tabs value={tab} onValueChange={setTab}>
        {/* Hero + primary toggle */}
        <section className="relative">
          <div className="absolute inset-0 h-64 sm:h-72">
            <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1400&q=80" alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 hero-overlay" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-14 pb-6">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-primary-foreground">Your journey begins here</h1>
            <p className="text-primary-foreground/80 mt-1 max-w-2xl">Search hundreds of travel sites at once to find the cheapest flights — all in one place on FlightDeals.in.</p>
            <TabsList className="mt-5 h-auto bg-card/90 backdrop-blur border border-border rounded-full p-1 flex max-w-full overflow-x-auto flex-nowrap justify-start" data-testid="flights-mode-tabs">
              <TabsTrigger value="search" className="rounded-full shrink-0 whitespace-nowrap px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold data-[state=active]:bg-accent data-[state=active]:text-accent-foreground" data-testid="flights-tab-search">
                <Search className="h-4 w-4 mr-1.5" /> Search Flights
              </TabsTrigger>
              <TabsTrigger value="smart" className="rounded-full shrink-0 whitespace-nowrap px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold data-[state=active]:bg-accent data-[state=active]:text-accent-foreground" data-testid="flights-tab-smart">
                <Sparkles className="h-4 w-4 mr-1.5" /> Smart Fares
              </TabsTrigger>
              <TabsTrigger value="weekends" className="rounded-full shrink-0 whitespace-nowrap px-3 sm:px-5 py-2 text-xs sm:text-sm font-semibold data-[state=active]:bg-accent data-[state=active]:text-accent-foreground" data-testid="flights-tab-weekends">
                <CalendarHeart className="h-4 w-4 mr-1.5" /> Weekend Finder
              </TabsTrigger>
            </TabsList>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* PRIMARY: native FlightDeals.in search (real offers via provider API → comparison + Smart Fares) */}
          <TabsContent value="search" className="mt-0" data-testid="flights-search-panel">
            <div className="mt-8 sm:mt-12 rounded-2xl border border-border bg-card p-4 sm:p-5">
              <div className="inline-flex items-center gap-1 rounded-full bg-secondary/40 border border-border p-1 mb-4" data-testid="search-mode-toggle-main">
                {[['airport', 'Airport / City', Plane], ['country', 'Country to country', Globe2]].map(([v, l, Ic]) => (
                  <button key={v} onClick={() => setMode(v)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${mode === v ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                    data-testid={`main-mode-${v}`}><Ic className="h-4 w-4" />{l}</button>
                ))}
              </div>
              {mode === 'airport' ? <FlightSearchBar /> : <CountrySearchBar />}
            </div>
            <div className="mt-3"><AffiliateDisclosure variant="footer" /></div>
            <FlightDiscovery currency={currency} />
          </TabsContent>

          {/* SMART FARES: kept for the upcoming provider-API integration */}
          <TabsContent value="smart" className="mt-0" data-testid="flights-smart-panel">
            <div className="mt-6 rounded-2xl border border-accent/30 bg-accent/[0.04] p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-accent" />
                <div>
                  <h2 className="font-display text-lg font-semibold">Smart Fares — FlightDeals Score</h2>
                  <p className="text-sm text-muted-foreground">Our transparent 0–100 fare ranking.</p>
                </div>
              </div>
              <div className="inline-flex items-center gap-1 rounded-full bg-card border border-border p-1 mb-4" data-testid="search-mode-toggle">
                {[['airport', 'Airport / City', Plane], ['country', 'Country to country', Globe2]].map(([v, l, Ic]) => (
                  <button key={v} onClick={() => setMode(v)}
                    className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors inline-flex items-center gap-1.5 ${mode === v ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                    data-testid={`search-mode-${v}`}><Ic className="h-4 w-4" />{l}</button>
                ))}
              </div>
              {mode === 'airport' ? <FlightSearchBar /> : <CountrySearchBar />}
            </div>
          </TabsContent>

          {/* WEEKEND FINDER: cheapest Fri–Sun round trips for a month */}
          <TabsContent value="weekends" className="mt-0" data-testid="flights-weekends-panel">
            <div className="mt-6">
              <WeekendFinder variant="section" />
            </div>
            <div className="mt-3"><AffiliateDisclosure variant="footer" /></div>
          </TabsContent>
        </section>
      </Tabs>

      {/* Shared trust row */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 grid gap-4 md:grid-cols-3">
        {FEATURES.map((f) => (
          <div key={f.t} className="rounded-2xl border border-border bg-card p-6">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/10 text-accent mb-3"><f.icon className="h-5 w-5" /></span>
            <p className="font-semibold">{f.t}</p>
            <p className="text-sm text-muted-foreground mt-1">{f.s}</p>
          </div>
        ))}
      </section>

      {/* Flight compensation */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" data-testid="flight-compensation-section">
        <div className="rounded-3xl border border-border bg-gradient-to-br from-accent/10 to-transparent p-6 sm:p-8">
          <div className="flex items-start gap-3">
            <span className="grid place-items-center h-11 w-11 rounded-xl bg-accent/15 text-accent shrink-0"><AlertTriangle className="h-5 w-5" /></span>
            <div>
              <h2 className="font-display text-2xl font-semibold">Is your flight cancelled or delayed?</h2>
              <p className="text-muted-foreground mt-1 max-w-2xl">You may be entitled to compensation of up to €600. Check your eligibility in a couple of minutes — no win, no fee.</p>
            </div>
          </div>
          <div className="mt-5 rounded-2xl border border-border bg-card p-4" data-testid="flight-compensation-widget-wrap">
            <TravelpayoutsWidget
              src="https://tpwidg.com/content?trs=564663&shmarker=766935&lang=en&powered_by=true&campaign_id=120&promo_id=8679"
              testid="flight-compensation-widget"
              minHeight={220}
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}
